import { rd } from '@passionware/monads';
import { useHotkeys } from 'react-hotkeys-hook';
import { featureFlags, AtellioFeatureGroupDefinition } from '../../features';
import { myAccessService } from '../../services/front/AccessService/AccessService.connected';
import { WithServices } from '../../platform/ts/services';
import { WithFeatureManagementService } from '../../services/front/FeatureManagementService/FeatureManagementService';
import { WithFeatureService } from '../../services/front/FeatureService/FeatureService';
import { FeatureName } from '../../platform/feature-management/feature-management';
import { get } from 'lodash';

export interface FeatureShortcutsWidgetProps
  extends WithServices<[WithFeatureManagementService, WithFeatureService]> {}

/**
 * Component that registers keyboard shortcuts for feature flags.
 * Only works if user has permission to manage feature flags.
 */
export function FeatureShortcutsWidget(props: FeatureShortcutsWidgetProps) {
  const { featureManagementService, featureService } = props.services;
  const can = myAccessService.useCan();
  const canManageFeatureFlags = rd.tryGet(
    can({ action: 'manage_feature_flags' })
  );
  const currentFlags = featureService.useFeatureFlags();

  // Collect all features with shortcuts
  const featuresWithShortcuts = featureFlags.groups.flatMap(group =>
    group.entries.filter(entry => 'shortcut' in entry && entry.shortcut)
  );

  // Register hotkeys for each feature with a shortcut
  // fixed array - safe to call useHotkeys in a loop
  featuresWithShortcuts.forEach(entry => {
    useHotkeys(
      get(entry, 'shortcut', ''),
      event => {
        event.preventDefault();

        // Only execute if user has permission
        if (!canManageFeatureFlags) {
          return;
        }

        // Get current feature flag values
        const flags = rd.tryGet(currentFlags);
        if (!flags) {
          return;
        }

        const featureId =
          entry.id as FeatureName<AtellioFeatureGroupDefinition>;
        const currentValue = flags[featureId];

        let newValue: boolean | string;

        if (entry.flagSpec.type === 'boolean') {
          // Toggle boolean values
          newValue = !currentValue;
        } else {
          // Cycle through enum options
          const options = entry.flagSpec.options;
          const currentIndex = options.indexOf(currentValue as any);
          const nextIndex = (currentIndex + 1) % options.length;
          newValue = options[nextIndex] as string;
        }

        // Schedule the action to update the feature flag in localStorage
        featureManagementService.scheduleAction({
          type: 'setFeatureFlag',
          source: 'frontend',
          featureId,
          value: newValue
        });

        // Save immediately
        featureManagementService.saveConfig();
      },
      {
        enableOnFormTags: true,
        enabled: canManageFeatureFlags
      },
      [canManageFeatureFlags, currentFlags]
    );
  });

  return null;
}
